import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MAX_REGIONS } from "../regionColors";
import type { Region } from "../types";
import { RegionThumbnail } from "./RegionThumbnail";

interface RegionListPanelProps {
  regions: Region[];
  imageSrc: string;
  onReset: () => void;
  onBack: () => void;
  onConfirm: () => void;
}

export const RegionListPanel = ({
  regions,
  imageSrc,
  onReset,
  onBack,
  onConfirm,
}: RegionListPanelProps) => {
  const { t } = useTranslation();
  const emptySlots = Math.max(0, MAX_REGIONS - regions.length);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--spacing14, 14px)",
      }}
    >
      <Box
        sx={{
          backgroundColor: "var(--alias-background-white, #fff)",
          borderRadius: "var(--card-small, 8px)",
          border: "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
          boxShadow: "var(--elevation-sm, 0 1px 4px rgba(0,0,0,0.07))",
          padding: "var(--spacing16, 16px) var(--spacing20, 20px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "var(--spacing14, 14px)",
          }}
        >
          <Typography
            sx={{
              fontSize: "var(--body-b3-12-regular-size, 12px)",
              fontWeight: 500,
            }}
          >
            {t("ldp_explore_image_region_count", { count: regions.length })}
          </Typography>
          {regions.length > 1 && (
            <Button
              variant="outlined"
              size="small"
              onClick={onReset}
              sx={{
                textTransform: "none",
                fontSize: "var(--body-b3-12-regular-size, 12px)",
                borderRadius: "var(--button-medium, 6px)",
                color: "var(--alias-text-on-white-secondary, #262626)",
                border:
                  "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
                "&:hover": {
                  border:
                    "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
                },
              }}
            >
              {t("ldp_explore_image_region_reset")}
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "var(--spacing12, 12px)",
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          {regions.map((region) => (
            <RegionThumbnail
              key={region.id}
              region={region}
              imageSrc={imageSrc}
            />
          ))}

          {Array.from({ length: emptySlots }).map((_, i) => (
            <Box
              key={`slot-${i}`}
              title={t("ldp_explore_image_region_slot_placeholder") as string}
              sx={{
                width: { xs: 80, md: 100 },
                height: 74,
                borderRadius: "var(--card-extra-small, 4px)",
                border:
                  "1.5px dashed var(--alias-stroke-on-white-default, #d9d9d9)",
                backgroundColor:
                  "var(--alias-surface-brand-light, rgba(0,159,228,0.02))",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "var(--spacing4, 4px)",
                flexShrink: 0,
              }}
            >
              <EditIcon
                sx={{
                  fontSize: 14,
                  color: "var(--alias-text-on-white-disable, #999)",
                }}
              />
              <Typography
                sx={{
                  fontSize: "var(--footnote-f1-10-regular-size, 9px)",
                  color: "var(--alias-text-on-white-disable, #999)",
                  textAlign: "center",
                  lineHeight: 1.3,
                  padding: "0 var(--spacing6, 6px)",
                }}
              >
                {t("ldp_explore_image_region_slot_draw")}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "var(--spacing12, 12px)",
        }}
      >
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{
            textTransform: "none",
            fontSize: "var(--body-b3-12-regular-size, 12px)",
            borderRadius: "var(--button-medium, 6px)",
            whiteSpace: "nowrap",
            color: "var(--alias-text-on-white-secondary, #262626)",
            border: "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
            "&:hover": {
              border: "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
            },
          }}
        >
          {t("ldp_explore_image_region_back")}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={regions.length === 0}
          endIcon={<ArrowForwardIcon />}
          sx={{
            textTransform: "none",
            fontSize: "var(--body-b3-12-regular-size, 12px)",
            borderRadius: "var(--button-medium, 6px)",
            whiteSpace: "nowrap",
            backgroundColor: "var(--alias-button-default, #009FE4)",
            "&:hover": {
              backgroundColor: "var(--alias-button-hover, #0088c7)",
            },
          }}
        >
          {t("ldp_explore_image_region_confirm")}
        </Button>
      </Box>
    </Box>
  );
};
